import { useEffect, useState } from "react";
import { X, Link2, Check, Loader2 } from "lucide-react";
import { cn } from "@/lib/cn";
import { togglePublic } from "@/lib/feedApi";

interface Props {
  postId: string;
  title: string;
  publicUrl: string;
  initialPublic: boolean;
  onClose: () => void;
  /** Notify parent of the new public state so the card stays in sync. */
  onChange?: (isPublic: boolean) => void;
}

const NETWORKS = [
  { key: "x", label: "X", color: "#000000", href: (u: string, t: string) => `https://twitter.com/intent/tweet?url=${u}&text=${t}` },
  { key: "linkedin", label: "LinkedIn", color: "#0A66C2", href: (u: string) => `https://www.linkedin.com/sharing/share-offsite/?url=${u}` },
  { key: "facebook", label: "Facebook", color: "#1877F2", href: (u: string) => `https://www.facebook.com/sharer/sharer.php?u=${u}` },
  { key: "whatsapp", label: "WhatsApp", color: "#25D366", href: (u: string, t: string) => `https://api.whatsapp.com/send?text=${t}%20${u}` },
];

export function ShareDialog({ postId, title, publicUrl, initialPublic, onClose, onChange }: Props) {
  const [isPublic, setIsPublic] = useState(initialPublic);
  const [toggling, setToggling] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const encodedUrl = encodeURIComponent(publicUrl);
  const encodedText = encodeURIComponent(title);

  async function toggle() {
    if (toggling) return;
    setToggling(true);
    try {
      const r = await togglePublic(postId);
      setIsPublic(r.public_share_enabled);
      onChange?.(r.public_share_enabled);
    } finally {
      setToggling(false);
    }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center px-4 pt-24" role="dialog" aria-modal="true">
      <button type="button" aria-label="Close" onClick={onClose} className="fixed inset-0 bg-overlay backdrop-blur-[2px]" />
      <div className="relative w-full max-w-sm rounded-[var(--radius-lg)] border border-border-subtle bg-surface shadow-[var(--shadow-4)]">
        <div className="flex items-center justify-between border-b border-border-subtle px-4 py-3">
          <h2 className="text-sm font-semibold text-fg">Share this post</h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="flex size-7 items-center justify-center rounded-[var(--radius-sm)] text-fg-muted hover:bg-surface-hover hover:text-fg [&_svg]:size-4"
          >
            <X />
          </button>
        </div>

        <div className="flex flex-col gap-4 p-4">
          {/* Public toggle */}
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm font-medium text-fg">Public sharing</div>
              <div className="text-xs text-fg-muted">Anyone with the link can view this post.</div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={isPublic}
              onClick={toggle}
              disabled={toggling}
              className={cn(
                "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors",
                isPublic ? "bg-accent" : "bg-border-default",
                toggling && "opacity-60",
              )}
            >
              <span
                className={cn(
                  "inline-block size-4 transform rounded-full bg-white shadow transition-transform",
                  isPublic ? "translate-x-4" : "translate-x-0.5",
                )}
              />
            </button>
          </div>

          <div className={cn("flex flex-col gap-3", !isPublic && "pointer-events-none opacity-50")}>
            <div className="grid grid-cols-2 gap-2">
              {NETWORKS.map((n) => (
                <a
                  key={n.key}
                  href={isPublic ? n.href(encodedUrl, encodedText) : undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  tabIndex={isPublic ? undefined : -1}
                  className="flex items-center justify-center gap-2 rounded-[var(--radius-md)] px-3 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
                  style={{ background: n.color }}
                >
                  {n.label}
                </a>
              ))}
            </div>

            <div className="flex items-center gap-2 rounded-[var(--radius-md)] border border-border-subtle bg-subtle px-2.5 py-1.5">
              <span className="min-w-0 flex-1 truncate text-xs text-fg-muted">{publicUrl}</span>
              <button
                type="button"
                onClick={copy}
                disabled={!isPublic}
                className="inline-flex shrink-0 items-center gap-1 rounded-[var(--radius-sm)] px-1.5 py-1 text-xs font-medium text-accent-text hover:bg-surface-hover [&_svg]:size-3.5"
              >
                {copied ? <Check /> : <Link2 />} {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </div>

          {!isPublic && (
            <p className="text-xs text-fg-subtle">
              {toggling ? (
                <span className="inline-flex items-center gap-1">
                  <Loader2 className="size-3 animate-spin" /> Updating…
                </span>
              ) : (
                "Turn on public sharing to get a shareable link."
              )}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
